/**
 * Coluna da esquerda: busca, filtros (o alerta Projetos e os tipos) e as
 * conversas na ordem da última mensagem, como no WhatsApp. Cada linha tem no
 * máximo UM sinal de cor (ver `sinalDaConversa`; o selo "Projetos" vence os
 * outros) e, embaixo da última mensagem, a frase da IA. O botão da prévia
 * abre o cartão inteiro sem abrir a conversa.
 */
import React, { useCallback, useRef, useState } from "react";
import { FiArrowLeft, FiSearch, FiUsers, FiX } from "react-icons/fi";

import { horaNaLista, iniciais, resumoDaUltimaMensagem, sinalDaConversa } from "../apresentacao";
import { ErroDaCentral } from "../comum";
import IconeDaIA from "../IconeDaIA";
import { ICONE_DA_MIDIA } from "../Mensagens";
import { SeloProjetos } from "./AlertaProjetos";
import PreviaFlutuante from "./PreviaFlutuante";
import {
  AvisoDaLista,
  Avatar,
  BotaoDaPrevia,
  BotaoIcone,
  Busca,
  ColunaLista,
  Contador,
  Contagem,
  Filtro,
  Filtros,
  HoraDoItem,
  ItemDaLista,
  ItemEsqueleto,
  ItensDaLista,
  LinhaDaIA,
  LinhaDaLista,
  LinhaDoItem,
  LinhaDoTitulo,
  NomeDoItem,
  Osso,
  PontoDeEstado,
  Rolagem,
  TextoDoItem,
  TopoDaLista,
  UltimaMensagem,
} from "./estilos";

/**
 * `consulta` = filtro da API (`interno` é a equipe; `desconhecido`, a
 * classificar); `contagem` = chave em `contagens`; `tom` = cor da contagem
 * quando ela pede ação.
 */
export const FILTROS = Object.freeze([
  { id: "todas", rotulo: "Todas", consulta: {} },
  {
    id: "projetos",
    rotulo: "Projetos",
    consulta: { alerta: "projetos" },
    contagem: "alerta_projetos",
    tom: "warning",
    ajuda: "Conversas com mensagem para a equipe de projetos ainda sem resposta",
  },
  { id: "clientes", rotulo: "Clientes", consulta: { classificacao: "cliente" }, contagem: "cliente", ajuda: "Conversas com clientes sobre um pedido" },
  { id: "obras", rotulo: "Obras", consulta: { classificacao: "obra" }, contagem: "obra", ajuda: "Grupos de obra e montagem" },
  { id: "equipe", rotulo: "Equipe", consulta: { classificacao: "interno" }, contagem: "interno", ajuda: "Conversas internas da equipe" },
  { id: "diretoria", rotulo: "Diretoria", consulta: { classificacao: "diretoria" }, contagem: "diretoria", ajuda: "Assuntos da diretoria" },
  {
    id: "a_classificar",
    rotulo: "A classificar",
    consulta: { classificacao: "desconhecido" },
    contagem: "desconhecido",
    tom: "warning",
    ajuda: "Ainda sem tipo: o texto é apagado em 5 dias se ninguém classificar",
  },
]);

export function filtroPeloId(id) {
  return FILTROS.find((f) => f.id === id) || FILTROS[0];
}

export function AvatarDaConversa({ conversa, tamanho, className }) {
  return (
    <Avatar $tamanho={tamanho} className={className} aria-hidden="true">
      {conversa?.grupo ? <FiUsers size={Math.round((tamanho || 46) * 0.42)} /> : iniciais(conversa?.nome)}
    </Avatar>
  );
}

function Sinal({ conversa }) {
  const sinal = sinalDaConversa(conversa);
  if (!sinal) return null;
  if (sinal.tipo === "projetos") return <SeloProjetos valor={sinal.valor} rotulo={sinal.rotulo} />;
  if (sinal.tipo === "contador") {
    return (
      <Contador $tom={sinal.tom} title={sinal.rotulo} aria-label={sinal.rotulo}>
        {sinal.valor}
      </Contador>
    );
  }
  return <PontoDeEstado $tom={sinal.tom} title={sinal.rotulo} role="img" aria-label={sinal.rotulo} />;
}

function LinhaDaConversa({ conversa, ativa, aoAbrir, previaAberta, aoAlternarPrevia, aoAtualizarItem }) {
  const gatilho = useRef(null);
  const resumo = resumoDaUltimaMensagem(conversa.ultima_mensagem, { grupo: conversa.grupo });
  const Icone = resumo.icone ? ICONE_DA_MIDIA[resumo.icone] : null;
  const situacao = String(conversa.previa?.situacao || "").trim();
  return (
    <LinhaDaLista>
      <ItemDaLista
        type="button"
        $ativo={ativa}
        aria-current={ativa ? "true" : undefined}
        data-conversa-id={conversa.id}
        onClick={() => aoAbrir(conversa)}
      >
        <AvatarDaConversa conversa={conversa} />
        <TextoDoItem>
          <LinhaDoItem>
            <NomeDoItem title={conversa.nome}>{conversa.nome}</NomeDoItem>
            <HoraDoItem>{horaNaLista(conversa.ultima_mensagem_em)}</HoraDoItem>
          </LinhaDoItem>
          <LinhaDoItem>
            <UltimaMensagem>
              {resumo.autor ? <span className="autor">{resumo.autor}:</span> : null}
              {Icone ? <Icone size={14} aria-hidden="true" /> : null}
              <span className="texto">{resumo.texto}</span>
            </UltimaMensagem>
            <Sinal conversa={conversa} />
          </LinhaDoItem>
          <LinhaDaIA $vazia={!situacao} title={situacao || undefined}>
            {situacao ? (
              <>
                <span className="cp-oculto">Prévia da IA: </span>
                {situacao}
              </>
            ) : (
              "Sem prévia ainda"
            )}
          </LinhaDaIA>
        </TextoDoItem>
      </ItemDaLista>
      <BotaoDaPrevia
        ref={gatilho}
        type="button"
        $aberto={previaAberta}
        aria-label={`Ver a prévia da IA de ${conversa.nome} sem abrir a conversa`}
        aria-haspopup="dialog"
        aria-expanded={previaAberta}
        title="Ver a prévia sem abrir a conversa"
        onClick={() => aoAlternarPrevia(conversa.id)}
      >
        <IconeDaIA tamanho={15} />
      </BotaoDaPrevia>
      {previaAberta ? (
        <PreviaFlutuante
          conversa={conversa}
          gatilho={gatilho}
          aoFechar={() => aoAlternarPrevia(null)}
          aoAbrirConversa={(c) => {
            aoAlternarPrevia(null);
            aoAbrir(c);
          }}
          aoAtualizarItem={aoAtualizarItem}
        />
      ) : null}
    </LinhaDaLista>
  );
}

function Esqueleto() {
  return (
    <ItensDaLista aria-hidden="true">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <ItemEsqueleto key={i}>
          <Osso $altura={46} $largura="46px" style={{ borderRadius: "50%" }} />
          <span>
            <Osso $altura={11} $largura={`${58 - i * 4}%`} />
            <Osso $altura={9} $largura={`${82 - i * 3}%`} />
            <Osso $altura={9} $largura={`${64 - i * 4}%`} />
          </span>
        </ItemEsqueleto>
      ))}
    </ItensDaLista>
  );
}

function textoSemResultado(filtro, busca) {
  if (busca) return `Nada encontrado para "${busca}".`;
  if (filtro.id === "a_classificar") return "Nenhuma conversa esperando classificação.";
  if (filtro.id === "projetos") return "Nenhum alerta Projetos aberto: tudo o que pediram à equipe de projetos foi respondido ou tratado.";
  if (filtro.id === "todas") return "Nenhuma conversa ainda. Com o monitor ligado, elas aparecem conforme chegam mensagens.";
  return `Nenhuma conversa em ${filtro.rotulo}.`;
}

export default function ListaDeConversas({
  conversas,
  contagens,
  carregando,
  erro,
  filtro,
  aoFiltrar,
  busca,
  aoBuscar,
  ativaId,
  aoAbrir,
  aoVoltar,
  aoAtualizarItem,
  escondida = false,
}) {
  const filtroAtual = filtroPeloId(filtro);
  const [previaAberta, setPreviaAberta] = useState(null);
  const alternarPrevia = useCallback(
    (id) => setPreviaAberta((atual) => (id === null || String(atual) === String(id) ? null : id)),
    [],
  );
  return (
    <ColunaLista aria-label="Lista de conversas" hidden={escondida} data-coluna-lista="">
      <TopoDaLista>
        <LinhaDoTitulo>
          <BotaoIcone type="button" aria-label="Voltar ao painel da Central" title="Voltar ao painel" onClick={aoVoltar}>
            <FiArrowLeft size={18} aria-hidden="true" />
          </BotaoIcone>
          <h1>Conversas</h1>
        </LinhaDoTitulo>
        <Busca>
          <FiSearch size={16} aria-hidden="true" />
          <input
            type="search"
            value={busca}
            onChange={(e) => aoBuscar(e.target.value)}
            placeholder="Buscar conversa ou pedido"
            aria-label="Buscar conversa ou pedido"
          />
          {busca ? (
            <button type="button" aria-label="Limpar a busca" onClick={() => aoBuscar("")}>
              <FiX size={16} aria-hidden="true" />
            </button>
          ) : null}
        </Busca>
        <Filtros role="group" aria-label="Filtrar as conversas">
          {FILTROS.map((f) => {
            const ativo = f.id === filtroAtual.id;
            const total = f.contagem ? Number(contagens?.[f.contagem]) || 0 : null;
            return (
              <Filtro
                key={f.id}
                type="button"
                $ativo={ativo}
                aria-pressed={ativo}
                title={f.ajuda}
                onClick={() => aoFiltrar(f.id)}
              >
                {f.rotulo}
                {total ? (
                  <Contagem $ativo={ativo} $tom={f.tom}>
                    {total}
                  </Contagem>
                ) : null}
              </Filtro>
            );
          })}
        </Filtros>
      </TopoDaLista>

      <Rolagem>
        {erro ? <div style={{ padding: 12 }}><ErroDaCentral erro={erro} /></div> : null}
        {!erro && carregando && !conversas ? <Esqueleto /> : null}
        {conversas && conversas.length === 0 ? (
          <AvisoDaLista>{textoSemResultado(filtroAtual, busca.trim())}</AvisoDaLista>
        ) : null}
        {conversas && conversas.length > 0 ? (
          <ItensDaLista>
            {conversas.map((c) => (
              <LinhaDaConversa
                key={c.id}
                conversa={c}
                ativa={String(c.id) === String(ativaId)}
                aoAbrir={aoAbrir}
                previaAberta={String(c.id) === String(previaAberta)}
                aoAlternarPrevia={alternarPrevia}
                aoAtualizarItem={aoAtualizarItem}
              />
            ))}
          </ItensDaLista>
        ) : null}
      </Rolagem>
    </ColunaLista>
  );
}
